import usePrevious from '@react-hook/previous';
import {
  addDays,
  format,
  getDate,
  getISOWeek,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWeekend,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { lighten } from 'polished';
import React from 'react';
import styled from 'styled-components';
import _ from 'underscore';

import { getDateFnsLocale } from '../../../../utils/dateUtils';
import { DateOnly } from '../../../utils/date/date-only';
import { MonthOnly } from '../../../utils/date/month-only';
import { reset } from '../../../utils/styled-reset';
import { itemDimension, SelectableItem } from '../styles/selectable-item';

const isDayEnabled = (date: DateOnly, minDate?: DateOnly, maxDate?: DateOnly) => {
  // Important that time is not set. Only compare date.
  if (minDate && isBefore(date.toDate(), minDate.toDate())) {
    return false;
  }

  if (maxDate && isAfter(date.toDate(), maxDate.toDate())) {
    return false;
  }

  return true;
};

const generateWeekDayNames = () => {
  const weekStart = startOfWeek(new Date(), { locale: getDateFnsLocale() });
  return _.range(7).map((e, i) =>
    format(addDays(weekStart, i), 'eeeeee', { locale: getDateFnsLocale() }),
  );
};

const generateWeeks = (
  currentMonth: MonthOnly,
  selectedDate: DateOnly,
  minDate?: DateOnly,
  maxDate?: DateOnly,
  rangeFrom?: DateOnly,
  rangeTo?: DateOnly,
) => {
  const currentMonthDate = currentMonth.toDate();

  // Always render 6 weeks starting at the week containing the first day of month.
  const firstWeekStart = startOfWeek(startOfMonth(currentMonthDate), {
    locale: getDateFnsLocale(),
  });
  const weekStarts = Array.from({ length: 6 }, (_, i) => addDays(firstWeekStart, i * 7));

  // Generate week days and meta data for each day.
  return weekStarts.map((startDate) => ({
    number: getISOWeek(startDate),
    days: Array.from({ length: 7 }).map((_, i) => {
      const date = addDays(startDate, i);

      const inRange =
        rangeFrom &&
        rangeTo &&
        (isSameDay(date, rangeFrom.toDate()) || isAfter(date, rangeFrom.toDate())) &&
        (isSameDay(date, rangeTo.toDate()) || isBefore(date, rangeTo.toDate()));

      const isStart = !!(rangeFrom && isSameDay(date, rangeFrom.toDate()));
      const isEnd = !!(rangeTo && isSameDay(date, rangeTo.toDate()));

      return {
        date,
        isEnabled: isDayEnabled(DateOnly.fromDate(date), minDate, maxDate),
        isWeekend: isWeekend(date),
        isCurrentMonth: isSameMonth(date, currentMonthDate),
        isSelected: isSameDay(date, selectedDate.toDate()),
        isInRange: Boolean(inRange),
        isRangeStart: isStart,
        isRangeEnd: isEnd,
      };
    }),
  }));
};

interface Week {
  number: number;
  days: WeekDay[];
}

interface WeekDay {
  date: Date;
  isEnabled: boolean;
  isWeekend: boolean;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isInRange?: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
}

interface Props {
  currentMonth: MonthOnly;
  selectedDate: DateOnly;
  minDate?: DateOnly;
  maxDate?: DateOnly;
  onNumWeeksChanged: (numWeeks: number) => void;
  onDateSelected: (date: DateOnly) => void;
  // Range visualization and controls (optional)
  rangeFrom?: DateOnly;
  rangeTo?: DateOnly;
  onRangeHandleClick?: (date: DateOnly, which: 'start' | 'end') => void;
  onWeekNumberClick?: (weekStartDate: DateOnly) => void;
}

type CalendarState = Week[];
type CalendarAction = { type: 'INIT'; weeks: Week[] } | { type: 'SELECT'; date: Date };

const Calendar = (props: Props) => {
  const [currentMonth, setCurrentMonth] = React.useState(props.currentMonth);
  const [selectedDate, setSelectedDate] = React.useState(props.selectedDate);
  const [weekDayNames, setWeekDayNames] = React.useState<string[]>([]);
  const [weeks, dispatchWeeks] = React.useReducer(weeksReducer, []);
  const numWeeksPrevious = usePrevious(weeks.length, 0);
  const [hoveredRangeKey, setHoveredRangeKey] = React.useState<number | null>(null);
  const [hoverSide, setHoverSide] = React.useState<'start' | 'end' | null>(null);

  const { onNumWeeksChanged: propsOnNumWeeksChanged } = props;

  // Generate week day names only once.
  React.useEffect(() => {
    setWeekDayNames(generateWeekDayNames());
  }, []);

  React.useEffect(() => {
    setCurrentMonth(props.currentMonth);
  }, [props.currentMonth]);

  React.useEffect(() => {
    if (weeks.length !== numWeeksPrevious) {
      propsOnNumWeeksChanged(weeks.length);
    }
  }, [propsOnNumWeeksChanged, numWeeksPrevious, weeks]);

  React.useEffect(() => {
    dispatchWeeks({
      type: 'INIT',
      weeks: generateWeeks(
        currentMonth,
        props.selectedDate,
        props.minDate,
        props.maxDate,
        props.rangeFrom,
        props.rangeTo,
      ),
    });
  }, [
    currentMonth,
    props.selectedDate,
    props.minDate,
    props.maxDate,
    props.rangeFrom,
    props.rangeTo,
  ]);

  /**
   * Selects the specified date. If date is not in current month
   * specified month will be rendered.
   * @param date Date to set as selected.
   */
  /**
   * Select specified date and update month when crossing month boundary.
   * @param {Date} date Date to set as selected
   */
  function selectDate(date: Date) {
    if (isSameMonth(selectedDate.toDate(), date)) {
      dispatchWeeks({
        type: 'SELECT',
        date,
      });
    } else {
      setCurrentMonth(MonthOnly.fromDate(date));
      setSelectedDate(DateOnly.fromDate(date));
    }

    setHoverSide(null);
    props.onDateSelected(DateOnly.fromDate(date));
  }

  function weeksReducer(state: CalendarState, action: CalendarAction) {
    switch (action.type) {
      case 'INIT':
        return action.weeks;

      case 'SELECT': {
        const weeks = [...state];

        // Unselect previous selected if any.
        let stop = false;
        for (const week of weeks) {
          if (stop) {
            break;
          }
          week.days.forEach((day, index) => {
            if (day.isSelected) {
              week.days[index] = {
                ...week.days[index],
                isSelected: false,
              };
              stop = true;
              return false;
            }
          });
        }

        // Set specified date as selected.
        const weekIndex = weeks.findIndex(
          (x) => x.days.findIndex((day) => isSameDay(day.date, action.date)) !== -1,
        );
        const dayIndex = weeks[weekIndex].days.findIndex((x) => isSameDay(x.date, action.date));
        weeks[weekIndex].days[dayIndex] = {
          ...weeks[weekIndex].days[dayIndex],
          isSelected: true,
        };

        return weeks;
      }
    }
  }

  return (
    <Component>
      <thead>
        <tr>
          <th>
            <HeaderCell>v</HeaderCell>
          </th>
          {weekDayNames.map((x) => (
            <th key={x}>
              <WeekDayHeaderCell>{x}</WeekDayHeaderCell>
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {weeks.map((week) => (
          <tr key={week.number}>
            <WeekNumberCell
              onClick={() => {
                const start = startOfWeek(new Date(week.days[0].date), {
                  locale: getDateFnsLocale(),
                });
                props.onWeekNumberClick?.(DateOnly.fromDate(start));
              }}
            >
              {week.number}
            </WeekNumberCell>

            {week.days.map((day) => (
              <td key={day.date.getTime()}>
                <DayWrapper
                  $hoverEnd={
                    !!day.isInRange &&
                    !day.isRangeStart &&
                    !day.isRangeEnd &&
                    hoveredRangeKey === day.date.getTime() &&
                    hoverSide === 'end'
                  }
                  $hoverStart={
                    !!day.isInRange &&
                    !day.isRangeStart &&
                    !day.isRangeEnd &&
                    hoveredRangeKey === day.date.getTime() &&
                    hoverSide === 'start'
                  }
                  $inRange={!!day.isInRange}
                  $isEnd={!!day.isRangeEnd}
                  $isStart={!!day.isRangeStart}
                >
                  <SelectableItem
                    boldText={day.isWeekend}
                    enabled={day.isEnabled}
                    lightColor={!day.isCurrentMonth}
                    selected={
                      day.isSelected &&
                      !(!!day.isRangeStart && !day.isRangeEnd) &&
                      !(!!day.isRangeEnd && !day.isRangeStart)
                    }
                    onClick={() => {
                      if (day.isEnabled) {
                        selectDate(day.date);
                      }
                    }}
                  >
                    {getDate(day.date)}
                  </SelectableItem>

                  {day.isInRange && !day.isRangeStart && !day.isRangeEnd && (
                    <RangeSplitOverlay
                      onMouseLeave={() => {
                        setHoveredRangeKey(null);
                        setHoverSide(null);
                      }}
                    >
                      <RangeHalf
                        aria-label="Set end"
                        title="Set end"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onRangeHandleClick?.(DateOnly.fromDate(day.date), 'end');
                        }}
                        onMouseEnter={() => {
                          setHoveredRangeKey(day.date.getTime());
                          setHoverSide('end');
                        }}
                      />
                      <RangeHalf
                        aria-label="Set start"
                        title="Set start"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onRangeHandleClick?.(DateOnly.fromDate(day.date), 'start');
                        }}
                        onMouseEnter={() => {
                          setHoveredRangeKey(day.date.getTime());
                          setHoverSide('start');
                        }}
                      />
                    </RangeSplitOverlay>
                  )}
                </DayWrapper>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Component>
  );
};

const Component = styled.table`
  ${reset}

  font-size: 12px;
  border-spacing: 0px;
  border-collapse: separate;
  user-select: none;
`;

const HeaderCell = styled.div`
  ${reset}
  ${itemDimension}
`;

const WeekDayHeaderCell = styled(HeaderCell)`
  font-weight: 600;
`;

const WeekNumberCell = styled.td`
  ${reset}
  ${itemDimension}

  font-weight: 400;

  background-color: ${(props) => lighten(0.03, props.theme.color.gray.lightest)};
  cursor: pointer;
`;

Calendar.styled = Component;

export { Calendar };

const Handles = styled.div`
  position: absolute;
  bottom: 2px;
  left: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  opacity: 0;
  transition: opacity 120ms ease-in-out;
`;

const DayWrapper = styled.div<{
  $inRange: boolean;
  $isStart: boolean;
  $isEnd: boolean;
  $hoverStart: boolean;
  $hoverEnd: boolean;
}>`
  position: relative;

  ${(p) =>
    p.$inRange &&
    !p.$isStart &&
    !p.$isEnd &&
    `
    background: ${p.theme.color.brand.lightest};
  `}

  ${(p) =>
    p.$isStart &&
    !p.$isEnd &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 2px;
      bottom: 2px;
      left: 2px;
      right: 0;
      background: ${p.theme.color.brand.dark};
      border-radius: 4px 0 0 4px;
    }
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: -0.5em;
      transform: translateY(-50%);
      border-top: 1em solid transparent;
      border-bottom: 1em solid transparent;
      border-left: 0.5em solid ${p.theme.color.brand.dark};
      z-index: 1;
    }
  `}

  ${(p) =>
    p.$isEnd &&
    !p.$isStart &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 2px;
      bottom: 2px;
      left: 0;
      right: 2px;
      background: ${p.theme.color.brand.dark};
      border-radius: 0 4px 4px 0;
    }
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: -0.5em;
      transform: translateY(-50%);
      border-top: 1em solid transparent;
      border-bottom: 1em solid transparent;
      border-right: 0.5em solid ${p.theme.color.brand.dark};
    }
  `}

  /* Hover visualization when splitting in-range day into halves */
  ${(p) =>
    p.$inRange &&
    !p.$isStart &&
    !p.$isEnd &&
    p.$hoverStart &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 2px;
      bottom: 2px;
      right: 0;
      left: calc(50% + 1px);
      background: ${p.theme.color.brand.dark};
      border-radius: 4px 0 0 4px;
    }
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: -0.7em;
      transform: translateY(-50%);
      border-top: 1em solid transparent;
      border-bottom: 1em solid transparent;
      border-left: 0.8em solid ${p.theme.color.brand.dark};
      z-index: 1;
    }
  `}

  ${(p) =>
    p.$inRange &&
    !p.$isStart &&
    !p.$isEnd &&
    p.$hoverEnd &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 2px;
      bottom: 2px;
      right: calc(50% + 1px);
      left: 0;
      background: ${p.theme.color.brand.dark};
      border-radius: 0 4px 4px 0;
    }
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: -0.7em;
      transform: translateY(-50%);
      border-top: 1em solid transparent;
      border-bottom: 1em solid transparent;
      border-right: 0.8em solid ${p.theme.color.brand.dark};
    }
  `}
  &:hover {
    /* show handles only on hover */
    ${Handles} {
      opacity: 1;
    }
  }
`;

/* Deprecated handle buttons removed; split overlay replaces them */

/* Invisible overlay that splits the in-range day into left/right clickable halves */
const RangeSplitOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
`;

const RangeHalf = styled.div`
  flex: 1 1 50%;
  cursor: pointer;
  /* ensure it sits above background visuals but below any potential future overlays */
  position: relative;
`;
